import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formLabelClasses, paperClasses } from '@mui/material';
import MuiDialog from '@mui/material/Dialog';
import MuiDialogActions from '@mui/material/DialogActions';
import MuiDialogTitle from '@mui/material/DialogTitle';
import { mergeSlotProps } from '@mui/material/utils';
import * as React from 'react';
import { Alert } from '../alert';
import { Box, FlexBox } from '../box/flexbox';
import { ScrollBox } from '../box/scroll';
import { Button, IconButton } from '../button';
import { useMounted } from '../hooks';
import { withPaperElevation } from '../paper';
import { styled } from '../styled';
import { RubiksCssBaseline } from '../theme';
import { firstLetterUppercase } from '../typography';
import { makeEnum } from '../utils';
import { Stepper } from './stepper';
const DialogActionIds = makeEnum('next', 'back', 'cancel', 'submit');
const DialogContext = React.createContext({});
const DialogHeader = styled(FlexBox)(({ theme }) => ({
    padding: theme.spacing(2, 3)
}));
const DialogTitle = styled(MuiDialogTitle)({
    flex: 1,
    minWidth: 0,
    padding: 0,
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    ...firstLetterUppercase
});
const StepperContainer = styled(FlexBox)(({ theme, $hasStickyContent, $contentPadding }) => ({
    paddingLeft: theme.spacing($contentPadding?.px ?? 3),
    paddingRight: theme.spacing($contentPadding?.px ?? 3),
    paddingTop: theme.spacing($hasStickyContent ? 3 : 0)
}));
const DialogContent = styled(Box)(({ theme, $contentPadding }) => ({
    padding: theme.spacing($contentPadding?.py ?? 3, $contentPadding?.px ?? 3)
}));
const NotificationAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(3)
}));
const ErrorAlert = styled(Alert)(({ theme }) => ({
    borderRadius: 0,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
}));
const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    justifyContent: 'space-between'
}));
const Component = React.forwardRef((props, ref) => {
    const { ownerState: _ownerState, ...componentProps } = props;
    return (_jsx(RubiksCssBaseline, { ref: ref, ...componentProps, sx: {
            display: undefined,
            width: undefined,
            height: undefined,
            backgroundColor: undefined
        } }));
});
export const Dialog = React.forwardRef((props, ref) => {
    const [children, setChildren] = React.useState(null);
    const mounted = useMounted();
    const { actions = [], error, children: _children, initLoading = false, title: Title, closeButtonLabel, contentPadding, stickyContent, stepper, pageId, dialogId, notification, ...muiProps } = props;
    const innerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => innerRef.current, []);
    const [dismissNotification, setDismissNotification] = React.useState(false);
    const memoActions = React.useMemo(() => actions.filter((a) => Boolean(a && !a.hidden)), [actions]);
    React.useEffect(() => {
        if (memoActions.filter((action) => action.type === 'submit')
            .length > 1) {
            throw new Error('[Dialog] Only one type of submit is allowed in the actions-props');
        }
    }, [memoActions]);
    React.useEffect(() => {
        // Render children after the actions which makes sure that the autoFocus is used properly
        // eslint-disable-next-line ml-rules/prefer-using-for-disposable
        const timeout = setTimeout(() => {
            if (mounted.current) {
                setChildren(_children);
            }
        }, 0);
        return () => clearTimeout(timeout);
    }, [_children, mounted]);
    React.useEffect(() => {
        if (!props.initLoading && props.open && innerRef.current) {
            // eslint-disable-next-line ml-rules/prefer-using-for-disposable
            const timeout = setTimeout(() => {
                innerRef.current
                    ?.querySelector(`.${paperClasses.root} .${formLabelClasses.root}`)
                    ?.focus();
            }, 16);
            return () => clearTimeout(timeout);
        }
        return;
    }, [innerRef, props.initLoading, props.open]);
    React.useEffect(() => {
        setDismissNotification(false);
    }, [notification?.type, notification?.message]);
    const submitAction = memoActions.find(({ type }) => type === 'submit');
    const onSubmit = submitAction?.action;
    const showNotification = notification?.type && notification?.message && !dismissNotification;
    return (_jsx(DialogContext.Provider, { value: { dialog: true, initLoading, dialogId, pageId }, children: _jsxs(MuiDialog, { ref: innerRef, id: dialogId, ...muiProps, fullWidth: true, component: Component, slotProps: {
                ...muiProps.slotProps,
                paper: withPaperElevation(mergeSlotProps(muiProps.slotProps?.paper, {
                    component: onSubmit ? 'form' : undefined,
                    // @ts-ignore noValidate is not in the type but can be passed to a form-element
                    noValidate: true
                }), 'XL')
            }, onSubmit: async (e) => {
                e.preventDefault();
                await onSubmit?.();
                if (submitAction?.preventClose) {
                    return;
                }
            }, children: [Title && (_jsxs(DialogHeader, { display: "flex", justifyContent: "space-between", alignItems: "center", children: [_jsx(DialogTitle, { variant: "h6", children: typeof Title === 'string' ? Title : _jsx(Title, {}) }), _jsx(IconButton, { icon: "IconClose", "aria-label": closeButtonLabel, tooltip: closeButtonLabel, sx: { alignSelf: 'flex-start' }, onClick: () => props.onClose?.({}, 'escapeKeyDown') })] })), stickyContent && _jsx(FlexBox, { stack: true, children: stickyContent }), stepper?.steps.length && stepper.steps.length > 1 && (_jsx(StepperContainer, { "$contentPadding": contentPadding, "$hasStickyContent": Boolean(stickyContent), children: _jsx(Stepper, { ...stepper }) })), _jsx(ScrollBox, { shadowType: "large", display: "block", flex: "1 1 auto", sx: { overflowY: 'auto' }, children: _jsxs(DialogContent, { "$contentPadding": contentPadding, children: [showNotification && (_jsx(NotificationAlert, { severity: notification.type, onClose: () => setDismissNotification(true), children: notification.message })), children] }) }), error && (_jsx(ErrorAlert, { severity: "error", children: error.message })), memoActions.length > 0 && (_jsxs(DialogActions, { children: [_jsx(FlexBox, { gap: 3, children: memoActions
                                .filter(({ id }) => id === 'cancel')
                                .map((action, i) => (_jsx(DialogActionComp, { ...action }, action.id ?? i))) }), _jsx(FlexBox, { gap: 3, children: memoActions
                                .filter(({ id }) => id !== 'cancel')
                                .map((action, i) => (_jsx(DialogActionComp, { variant: action.id === 'submit'
                                    ? 'contained'
                                    : 'text', ...action }, action.id ?? i))) })] }))] }) }));
});
function DialogActionComp(props) {
    const { action, title, type = 'button', color, disabled, loading, variant } = props;
    return (_jsx(Button, { variant: variant, color: color ?? 'inherit', type: type, autoFocus: type === 'submit', disabled: disabled, loading: loading, onClick: () => {
            if (type === 'submit') {
                return;
            }
            void action();
        }, children: title }));
}
export function useIsInDialog() {
    const { dialog } = React.useContext(DialogContext);
    return Boolean(dialog);
}
export function useDialogInitLoading() {
    const { initLoading } = React.useContext(DialogContext);
    return Boolean(initLoading);
}
export const useDialogId = () => {
    const { dialogId, pageId } = React.useContext(DialogContext);
    return { dialogId, pageId };
};
export const dialogStyle = {
    MuiDialog: {
        styleOverrides: {
            paperWidthXs: {
                maxWidth: 444
            },
            paperWidthSm: {
                maxWidth: 600
            },
            paperWidthMd: {
                maxWidth: 900
            },
            paperWidthLg: {
                maxWidth: 1200
            },
            paperWidthXl: {
                maxWidth: 1536
            }
        }
    }
};
